package com.routexk.quiz.funnel

import kotlin.math.max
import kotlin.math.min
import kotlin.math.roundToInt

/**
 * Result of one of the two mutually-exclusive scenarios. Scenario A takes the
 * freed time back as hours; Scenario B fills it with more stops (earnings).
 * Freed time is spent one way OR the other — never counted twice.
 */
data class ScenarioA(
    val hoursSavedPerYear: Int,
    val workingDaysReturned: Int,
    val fuelSavedUsd: Int,
    val fuelSavedPct: Int,
    val annualMiles: Int,
    val deductionUsd: Int,
    val taxSavingsUsd: Int,
)

data class ScenarioB(
    val extraEarningsUsd: Int,
    val extraStopsPerShift: Double,
    val extraStopsPct: Int,
    val annualMiles: Int,
    val deductionUsd: Int,
    val taxSavingsUsd: Int,
)

/**
 * View model consumed by the results / report / paywall screens.
 * The scenario-correct figures come from [scenarioA]/[scenarioB]; the flat
 * fields are convenience mirrors wired to the existing UI slots.
 */
data class FunnelEstimate(
    val taxDeductionUsd: Int,
    val taxSavingsUsd: Int,
    val moneyIncreaseYearUsd: Int,
    val hoursSavedYear: Int,
    val workingDaysReturned: Int,
    val fuelSavingsPct: Int,
    val fuelSavingsYearUsd: Int,
    val timeSavingPotentialPct: Int,
    val fuelSavingPotentialPct: Int,
    val moneyIncreasePotentialPct: Int,
    // Full engine output
    val mileageReductionPct: Int,
    val baselineAnnualMiles: Int,
    val scenarioA: ScenarioA,
    val scenarioB: ScenarioB,
    val warnings: List<String>,
    /** False when an IMPLAUSIBLE_* warning makes the numbers untrustworthy (Step 10 rule 5). */
    val reliable: Boolean,
)

/**
 * Quiz answers -> results figures, per routexk-quiz-results-spec.md.
 * Deterministic: same answers always yield the same numbers.
 */
object EstimateCalculator {

    // Step 2 — fixed constants
    private const val WEEKS_PER_YEAR = 50
    private const val DRIVING_SHARE_OF_TIME = 0.55
    private const val PLANNING_MINUTES_AFTER = 12.0
    private const val DEMAND_AVAILABILITY = 0.75
    private const val FALLBACK_FUEL_COST_PER_MILE = 0.16
    // Matches the "$0.76/mile" copy shown on the results screen
    private const val IRS_MILEAGE_RATE_2026 = 0.76

    // Step 9 — 2026 tax constants (IRS Rev. Proc. 2025-32), single filer assumed
    private const val STD_DEDUCTION = 16_100.0
    private const val SE_TAX_BASE = 0.9235
    private const val SE_TAX_RATE = 0.153
    private const val QBI_RATE = 0.20
    private const val NON_VEHICLE_EXPENSE = 0.04
    private const val DEFAULT_STATE_RATE = 0.045

    // Federal brackets 2026, single (upper thresholds); spec uses 10/12/22/24
    private val FED_BRACKETS = listOf(
        0.0 to 0.10,
        12_400.0 to 0.12,
        50_400.0 to 0.22,
        105_700.0 to 0.24,
    )

    private val ZERO_TAX_STATES = setOf("AK", "FL", "NV", "NH", "SD", "TN", "TX", "WA", "WY")
    private val STATE_RATES = mapOf("NY" to 0.055)

    fun calculate(answers: FunnelAnswers): FunnelEstimate {
        // Step 1 — map answers to single point estimates
        val stops = point(QuizStepId.STOPS, answers.stopsIndex, 35.0)
        val milesPerShift = point(QuizStepId.MILES, answers.milesIndex, 75.0)
        val hoursPerShift = point(QuizStepId.HOURS, answers.hoursIndex, 8.5)
        val daysPerWeek = point(QuizStepId.DAYS, answers.daysIndex, 4.5)
        val hourlyRate = point(QuizStepId.EARNINGS, answers.earningsIndex, 25.0)
        // Fuel "I don't know" is stored as the -1 sentinel -> null
        val reportedFuel = point(QuizStepId.FUEL, answers.fuelIndex, -1.0).takeIf { it >= 0.0 }
        val state = parseState(answers.city)

        val warnings = mutableListOf<String>()
        var reliable = true

        val shifts = daysPerWeek * WEEKS_PER_YEAR
        val baselineAnnualMiles = milesPerShift * shifts
        val weeklyMiles = milesPerShift * daysPerWeek

        // Step 3 — validation
        val milesPerStop = if (stops > 0) milesPerShift / stops else 0.0
        if (milesPerStop < 0.15) { warnings += "IMPLAUSIBLE_LOW_MILES_PER_STOP"; reliable = false }
        if (milesPerStop > 40) { warnings += "IMPLAUSIBLE_HIGH_MILES_PER_STOP"; reliable = false }
        val stopsPerHour = if (stops > 0) (hoursPerShift * 60) / stops else 0.0
        if (stopsPerHour < 2) { warnings += "IMPLAUSIBLE_STOPS_PER_HOUR"; reliable = false }

        var fuelWarningFired = false
        if (reportedFuel != null && weeklyMiles > 0) {
            val perMile = reportedFuel / weeklyMiles
            if (perMile < 0.05 || perMile > 0.60) {
                warnings += "IMPLAUSIBLE_FUEL_PER_MILE"
                fuelWarningFired = true
            }
        }

        val planningBefore = when {
            stops < 25 -> 45.0
            stops < 75 -> 52.0
            else -> 60.0
        }
        val delta = when {
            stops < 8 -> 0.08
            stops < 15 -> 0.15
            stops < 25 -> 0.20
            stops < 60 -> 0.25
            stops < 120 -> 0.28
            else -> 0.30
        }

        if (planningBefore > (hoursPerShift * 60) * 0.35) warnings += "PLANNING_TIME_EXCEEDS_SHIFT_SHARE"

        // Step 4 — baseline fuel
        val annualFuel = if (reportedFuel == null || fuelWarningFired) {
            baselineAnnualMiles * FALLBACK_FUEL_COST_PER_MILE
        } else {
            reportedFuel * WEEKS_PER_YEAR
        }
        val fuelCostPerMile = if (baselineAnnualMiles > 0) annualFuel / baselineAnnualMiles else FALLBACK_FUEL_COST_PER_MILE

        // Step 5 — decompose time per stop
        val productiveMin = hoursPerShift * 60 - planningBefore
        val minPerStop = if (stops > 0) productiveMin / stops else 0.0
        val driveMinPerStop = minPerStop * DRIVING_SHARE_OF_TIME
        val dwellMinPerStop = minPerStop * (1 - DRIVING_SHARE_OF_TIME)

        // Step 6 — apply optimization to the drive portion only
        val driveMinPerStopAfter = driveMinPerStop * (1 - delta)
        val minPerStopAfter = driveMinPerStopAfter + dwellMinPerStop

        // Step 7 — Scenario A: take the time back
        val shiftMinutesAfter = stops * minPerStopAfter + PLANNING_MINUTES_AFTER
        val minutesSavedPerShift = hoursPerShift * 60 - shiftMinutesAfter
        val hoursSavedPerYear = minutesSavedPerShift * shifts / 60
        val workingDaysReturned = if (hoursPerShift > 0) hoursSavedPerYear / hoursPerShift else 0.0
        val annualMilesA = milesPerShift * (1 - delta) * shifts
        val fuelSaved = (baselineAnnualMiles - annualMilesA) * fuelCostPerMile
        val fuelSavedPct = delta

        // Step 8 — Scenario B: fill the time with work
        val availableMinutes = hoursPerShift * 60 - PLANNING_MINUTES_AFTER
        val stopsCapacity = if (minPerStopAfter > 0) availableMinutes / minPerStopAfter else 0.0
        val extraStops = max(0.0, (stopsCapacity - stops) * DEMAND_AVAILABILITY)
        val revenuePerStop = if (stops > 0) (hourlyRate * hoursPerShift) / stops else 0.0
        val extraEarnings = extraStops * shifts * revenuePerStop
        val annualMilesB = baselineAnnualMiles // more stops at fewer miles each ~ cancels

        // Step 9 — tax, per scenario (uses that scenario's annual miles)
        val gross = hourlyRate * hoursPerShift * shifts
        val profitBefore = gross * (1 - NON_VEHICLE_EXPENSE)
        val stateRate = stateRate(state)

        fun taxFor(annualMiles: Double): Pair<Double, Double> {
            val deduction = annualMiles * IRS_MILEAGE_RATE_2026
            val profitAfter = profitBefore - deduction
            val savings = totalTax(profitBefore, stateRate) - totalTax(profitAfter, stateRate)
            return deduction to savings
        }
        val (deductionA, taxSavingsA) = taxFor(annualMilesA)
        val (deductionB, taxSavingsB) = taxFor(annualMilesB)

        val scenarioA = ScenarioA(
            hoursSavedPerYear = hoursSavedPerYear.roundToInt(),
            workingDaysReturned = workingDaysReturned.roundToInt(),
            fuelSavedUsd = fuelSaved.roundToInt(),
            fuelSavedPct = (fuelSavedPct * 100).roundToInt(),
            annualMiles = annualMilesA.roundToInt(),
            deductionUsd = deductionA.roundToInt(),
            taxSavingsUsd = taxSavingsA.roundToInt(),
        )
        val scenarioB = ScenarioB(
            extraEarningsUsd = extraEarnings.roundToInt(),
            extraStopsPerShift = (extraStops * 10).roundToInt() / 10.0,
            extraStopsPct = (if (stops > 0) extraStops / stops * 100 else 0.0).roundToInt(),
            annualMiles = annualMilesB.roundToInt(),
            deductionUsd = deductionB.roundToInt(),
            taxSavingsUsd = taxSavingsB.roundToInt(),
        )

        return FunnelEstimate(
            taxDeductionUsd = scenarioA.deductionUsd,
            taxSavingsUsd = scenarioA.taxSavingsUsd,
            moneyIncreaseYearUsd = scenarioB.extraEarningsUsd,
            hoursSavedYear = scenarioA.hoursSavedPerYear,
            workingDaysReturned = scenarioA.workingDaysReturned,
            fuelSavingsPct = scenarioA.fuelSavedPct,
            fuelSavingsYearUsd = scenarioA.fuelSavedUsd,
            timeSavingPotentialPct = potential(stops / 240.0, base = 62, span = 28),
            fuelSavingPotentialPct = potential((reportedFuel ?: 75.0) / 120.0, base = 48, span = 26),
            moneyIncreasePotentialPct = potential(hourlyRate / 50.0, base = 78, span = 18),
            mileageReductionPct = (delta * 100).roundToInt(),
            baselineAnnualMiles = baselineAnnualMiles.roundToInt(),
            scenarioA = scenarioA,
            scenarioB = scenarioB,
            warnings = warnings,
            reliable = reliable,
        )
    }

    /** Full tax stack (SE tax + federal + state), computed twice and subtracted. */
    private fun totalTax(profit: Double, stateRate: Double): Double {
        if (profit <= 0) return 0.0
        val seTax = profit * SE_TAX_BASE * SE_TAX_RATE
        val agi = profit - 0.5 * seTax
        val qbi = min(QBI_RATE * agi, QBI_RATE * max(agi - STD_DEDUCTION, 0.0))
        val taxableInc = max(agi - STD_DEDUCTION - qbi, 0.0)
        val fed = federalTax(taxableInc)
        val stateTax = stateRate * max(agi - STD_DEDUCTION, 0.0)
        return seTax + fed + stateTax
    }

    private fun federalTax(taxableInc: Double): Double {
        var tax = 0.0
        for (i in FED_BRACKETS.indices) {
            val (lower, rate) = FED_BRACKETS[i]
            if (taxableInc <= lower) break
            val upper = FED_BRACKETS.getOrNull(i + 1)?.first ?: Double.MAX_VALUE
            val amountInBracket = min(taxableInc, upper) - lower
            tax += amountInBracket * rate
        }
        return tax
    }

    private fun stateRate(state: String?): Double = when {
        state == null -> DEFAULT_STATE_RATE
        state in ZERO_TAX_STATES -> 0.0
        else -> STATE_RATES[state] ?: DEFAULT_STATE_RATE
    }

    /** Pull a 2-letter US state code out of a free-text city answer like "New York, NY, USA". */
    private fun parseState(city: String?): String? {
        if (city.isNullOrBlank()) return null
        return Regex("\\b([A-Z]{2})\\b").findAll(city)
            .map { it.groupValues[1] }
            .firstOrNull { it in US_STATES }
    }

    private fun point(stepId: QuizStepId, index: Int?, fallback: Double): Double {
        val midpoints = QuizDefinition.step(stepId).midpoints ?: return fallback
        return index?.let { midpoints.getOrNull(it) } ?: fallback
    }

    private fun potential(ratio: Double, base: Int, span: Int): Int =
        base + (span * ratio.coerceIn(0.0, 1.0)).roundToInt()

    private val US_STATES = setOf(
        "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "ID", "IL", "IN", "IA",
        "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
        "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC", "SD", "TN", "TX", "UT", "VT",
        "VA", "WA", "WV", "WI", "WY", "DC",
    )
}

fun Int.asMoney(): String {
    val neg = this < 0
    val s = kotlin.math.abs(this).toString()
    val sb = StringBuilder()
    s.forEachIndexed { i, c ->
        sb.append(c)
        val remaining = s.length - i - 1
        if (remaining > 0 && remaining % 3 == 0) sb.append(',')
    }
    return (if (neg) "-$" else "$") + sb
}
